import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { extractionService } from '@/services/extraction.service'
import type { ExtractedRiskField } from '@/types'
import { FileText } from 'lucide-react'
import JsonView from '@microlink/react-json-view'

interface RiskProfilePanelProps {
  submissionId: string
}

export function RiskProfilePanel({ submissionId }: RiskProfilePanelProps) {
  const queryClient = useQueryClient()

  const { data: fields, isLoading } = useQuery({
    queryKey: ['extracted-fields', submissionId],
    queryFn: () => extractionService.getSubmissionFields(submissionId)
  })

  // Subscribe to real-time updates
  useEffect(() => {
    const subscription = extractionService.subscribeToExtractedFields(
      submissionId,
      () => {
        queryClient.invalidateQueries({ queryKey: ['extracted-fields', submissionId] })
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [submissionId, queryClient])

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 rounded bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  const allFields = fields || []
  const rawResponse = extractionService.getLastRawResponse()

  // Convert to simple JSON format for display
  const jsonOutput = allFields.reduce((acc: Record<string, string | null>, field: ExtractedRiskField) => {
    acc[field.field_label] = field.extracted_value
    return acc
  }, {})

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b p-4">
        <h2 className="font-semibold">Extracted JSON</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {allFields.length} fields extracted
        </p>
      </div>

      {/* JSON Output */}
      <div className="flex-1 overflow-auto p-4">
        {allFields.length === 0 && !rawResponse ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No fields extracted yet.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Process a document to extract fields.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Extracted Fields */}
            {allFields.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground mb-2">Extracted Fields</h3>
                <JsonView
                  src={jsonOutput}
                  theme="github"
                  collapsed={false}
                  displayDataTypes={false}
                  enableClipboard={true}
                  style={{ fontSize: '12px', background: 'transparent' }}
                />
              </div>
            )}

            {/* Raw Nanonets Response */}
            {rawResponse && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground mb-2">Raw Nanonets Response</h3>
                <JsonView
                  src={rawResponse as object}
                  theme="github"
                  collapsed={1}
                  displayDataTypes={false}
                  enableClipboard={true}
                  style={{ fontSize: '11px', background: 'transparent' }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
